import type { Language } from '@eformulieren/engine'
import { type AdviesToestand, type DelingState, type Side, INVITATION_VALID_MS, isOpenAdvies } from '@eformulieren/model'
import { type Bilingual, formatDateTime } from '../language/text.js'

/**
 * Wat de dialoog "Delen" en de lijst "Wie heeft toegang" tonen, zonder DOM (R-64, R-65, R-65a,
 * R-65b, R-65c, R-66, R-66a, R-66b, R-184, besluiten 1 tot en met 7 van 30 oktober 2026).
 *
 * Hier staan de vormen die de pagina aanlevert en de zinnen die het scherm ervan maakt: de
 * samenvatting die meeloopt, de stand van een regel met datum en reden, wie uitnodigde als rol, en de
 * groepen van de lijst. Het scherm zelf staat in `ShareDialog.tsx`; wat er per sectie te geven valt
 * beslist de pagina (`apps/runtime/pagina/sharing.ts`), uit de projectie van de pod.
 */

/** Waarom schrijven bij een sectie niet kan; de reden staat bij de sectie en niet achteraf in een fout. */
export type WriteBlock =
  /** Een toetssectie deelt u alleen om te lezen (R-65b). */
  | 'toetssectie'
  /** Een bronsectie toont gegevens uit een eerder formulier; daar schrijft niemand (R-56). */
  | 'bronsectie'
  /** Wie toetst, geeft nergens schrijfrecht (R-65c). */
  | 'toetser'
  /** U leest deze sectie zelf alleen, en geeft nooit meer dan u hebt (R-65a). */
  | 'zelf-lezen'

/** Een sectie die de deler kan delen, zoals de pagina haar aanlevert. */
export interface ShareSection {
  readonly id: string
  readonly titel: string
  /** Het nummer in het formulier, als de pagina het meegeeft. */
  readonly nummer?: number
  /** Of de deler hier schrijfrecht kan geven. */
  readonly magBewerken: boolean
  /** Waarom niet, als `magBewerken` onwaar is. */
  readonly schrijfReden?: WriteBlock
  /** Nu verborgen in het formulier door een zichtbaarheidsregel: alleen onder "Alle secties tonen". */
  readonly verborgen?: boolean
  /** Door de inrichter uitgesloten van advies (`uitgeslotenSecties`, ontwerp 7.4a). */
  readonly adviesUitgesloten?: boolean
}

/** Wat de genodigde mag: de rolkeuze van besluit 1. Advies geven alleen aan de interne kant. */
export type ShareRole = 'meelezen' | 'meeschrijven' | 'advies'

/**
 * Waar een adviesvraag over gaat. Een veld draagt zijn label voor op het scherm ("vraag 2.2
 * Begroting op hoofdlijnen"), want alleen wie de dialoog opent weet hoe de vraag in beeld heet.
 */
export type ShareAnchor =
  | { readonly soort: 'formulier' }
  | { readonly soort: 'sectie'; readonly sectieId: string }
  | {
      readonly soort: 'veld'
      readonly sectieId: string
      readonly veldId: string
      readonly label: string
      /** Een criteriumveld in een toetssectie: dan gaat het anker als `criterium` naar de pod (R-184). */
      readonly criterium?: boolean
    }

/** Hoe de dialoog opengaat: op welk tabblad, met welke rol, en bij advies over welk onderwerp. */
export interface ShareStart {
  readonly tab?: 'uitnodigen' | 'toegang'
  readonly rol?: ShareRole
  readonly anker?: ShareAnchor
  /** Voorgekozen secties, bijvoorbeeld bij "Deze sectie delen". */
  readonly secties?: readonly string[]
}

/** Wat de interne kant bij advies kan kiezen, uit `GET /advies` (alleen als `kanVragen`). */
export interface AdviceOffer {
  /** Of de definitie een adviseur buiten NWO toestaat (`adviseurBuitenNwo`). */
  readonly buitenNwo: boolean
}

/** Wat de dialoog verstuurt. */
export type InviteRequest =
  | {
      readonly soort: 'deling'
      readonly email: string
      readonly rechten: Readonly<Record<string, 'lezen' | 'bewerken'>>
    }
  | {
      readonly soort: 'advies'
      readonly email: string
      readonly vraag: string
      readonly anker: ShareAnchor
      readonly adviseurKant: Side
      readonly gedeeld: readonly string[]
      /** Een datum als `2026-12-12`, of niets. */
      readonly termijn: string | null
    }

/** Een melding na een weigering van de pod, met wat ze betreft: secties, of een veld. */
export interface ShareNotice {
  readonly tekst: string
  readonly secties: readonly string[]
  readonly veld?: 'email' | 'vraag' | 'adviseurKant'
}

export type InvitationOutcome =
  /** Verstuurd; `id` is de regel in het overzicht (de uitnodiging), als de pod hem noemde. */
  | { readonly soort: 'verstuurd'; readonly id?: string }
  | { readonly soort: 'geweigerd'; readonly melding: ShareNotice }

/** De uitkomst van een handeling op een regel: wijzigen, intrekken, opnieuw uitnodigen, herinneren. */
export type ActionOutcome = { readonly soort: 'gelukt' } | { readonly soort: 'geweigerd'; readonly melding: ShareNotice }

/** Wie uitnodigde, als rol (besluit 5); `u` als de kijker het zelf deed. */
export type SharerRole = 'aanvrager' | 'gemachtigde' | 'medewerker-nwo' | 'u'

/**
 * Een regel uit het overzicht van de pod (R-66). Het adres staat er alleen als de pod het meestuurt:
 * voor de partij van de deler en voor de ontvanger zelf (besluit 5). `intrekbaar` en `wijzigbaar`
 * zegt de pod; ontbreken ze (een pod van vóór besluit 2), dan geldt intrekken bij een eigen lopende
 * regel en geen wijzigen.
 */
export interface ShareLine {
  readonly id: string
  readonly email?: string
  readonly rechten: Readonly<Record<string, 'lezen' | 'bewerken'>>
  readonly delerKant: Side
  readonly stand: DelingState
  readonly aangemaaktOp: string
  readonly geldigTot: string
  readonly sinds: string | null
  readonly eigen: boolean
  readonly delerRol?: SharerRole
  readonly intrekbaar?: boolean
  readonly wijzigbaar?: boolean
}

/** Een toekenning van de eigen kant uit het processysteem (besluit 7): alleen de rol. */
export interface OwnGrant {
  readonly rol: string
  readonly isU: boolean
}

/** Een adviesverzoek naast zijn regel (interne kant, gekoppeld via `uitnodigingId`). */
export interface AdviceLine {
  readonly id: string
  readonly uitnodigingId: string
  readonly toestand: AdviesToestand
  readonly anker: { readonly soort: 'formulier' | 'sectie' | 'veld' | 'criterium'; readonly label: string }
  readonly termijn: string | null
  readonly adviseurKant: Side
  readonly gedeeld: readonly string[]
}

const T = {
  en: { nl: 'en', en: 'and' },
  deze: { nl: 'Deze persoon', en: 'This person' },
  schrijven: { nl: 'kan schrijven in {a}', en: 'can write in {a}' },
  lezen: { nl: 'kan lezen in {a}', en: 'can read {a}' },
  enLezen: { nl: ', en lezen in {a}', en: ', and read {a}' },
  rest: { nl: 'Andere secties ziet deze persoon niet.', en: 'This person does not see other sections.' },
  geldig: {
    nl: 'De uitnodiging komt per e-mail en is {d} dagen geldig.',
    en: 'The invitation is sent by email and is valid for {d} days.',
  },
  geenSectie: {
    nl: '{wie} krijgt nog geen toegang: kies minstens één sectie.',
    en: '{wie} gets no access yet: choose at least one section.',
  },
  advies: {
    nl: 'U deelt {secties} met {wie} om advies. Deze persoon kan lezen en antwoorden, niet wijzigen. De uitnodiging komt per e-mail.',
    en: 'You share {secties} with {wie} for advice. This person can read and answer, not change anything. The invitation is sent by email.',
  },
  deZePersoon: { nl: 'deze persoon', en: 'this person' },
  sectie: { nl: 'sectie {s}', en: 'section {s}' },
  secties: { nl: 'de secties {s}', en: 'the sections {s}' },
  geenAdviesSectie: {
    nl: 'Kies bij Wat u deelt minstens één sectie.',
    en: 'Choose at least one section under What you share.',
  },
  alleLezen: { nl: 'kan alle {n} secties lezen', en: 'can read all {n} sections' },
  alleSchrijven: { nl: 'kan schrijven in alle {n} secties', en: 'can write in all {n} sections' },
  deelsSchrijven: { nl: 'kan schrijven in {w} van de {n} secties, en lezen in {a}', en: 'can write in {w} of the {n} sections, and read {a}' },
} as const satisfies Readonly<Record<string, Bilingual>>

/**
 * Waarom een sectie bij "Alle secties" alleen te lezen is, kort en per groep (besluit 1): de reden
 * staat bij de secties in beeld vóór het versturen en niet als fout achteraf (R-65a, R-65b, R-65c,
 * R-56). `{s}` wordt de lijst secties.
 */
// Met "want" en niet met een dubbele punt: een sectietitel heeft er soms zelf een.
const READ_ONLY_GROUP: Readonly<Record<WriteBlock, Bilingual>> = {
  bronsectie: { nl: 'Alleen lezen in {s}, want daar staan gegevens uit een eerder formulier.', en: 'Read only in {s}, because it holds data from an earlier form.' },
  toetssectie: { nl: 'Alleen lezen in {s}, want een toetssectie deelt u alleen om te lezen.', en: 'Read only in {s}, because a review section can only be shared for reading.' },
  toetser: { nl: 'Alleen lezen in {s}, want een toetser geeft geen schrijfrecht.', en: 'Read only in {s}, because a reviewer does not give write access.' },
  'zelf-lezen': { nl: 'Alleen lezen in {s}, want u leest daar zelf alleen.', en: 'Read only in {s}, because you can only read there yourself.' },
}

const HIDDEN_NOT_SHARED: Readonly<Record<'een' | 'meer', Bilingual>> = {
  een: { nl: 'Niet {s}, want die is nu verborgen in het formulier.', en: 'Not {s}, because it is currently hidden in the form.' },
  meer: { nl: 'Niet {s}, want die zijn nu verborgen in het formulier.', en: 'Not {s}, because they are currently hidden in the form.' },
}

const HIDDEN_KEPT: Readonly<Record<'een' | 'meer', Bilingual>> = {
  een: { nl: 'In {s} blijft het recht dat er al was, want die is nu verborgen in het formulier.', en: 'In {s} the existing access stays, because it is currently hidden in the form.' },
  meer: { nl: 'In {s} blijft het recht dat er al was, want die zijn nu verborgen in het formulier.', en: 'In {s} the existing access stays, because they are currently hidden in the form.' },
}

/**
 * Wat "Alle secties" geeft (opdracht van 2 november 2026, ontwerp 6.4): elke sectie die nu in het
 * formulier staat, en bij meeschrijven schrijven waar de deler dat kan geven en anders lezen. Een
 * sectie die een regel nu verbergt valt erbuiten, net als bij advies over het hele formulier; wie haar
 * wil delen, kiest Alleen bepaalde secties. Het wordt bij elke render en bij versturen opnieuw
 * afgeleid, zodat een andere rol nooit een oude stand achterlaat.
 */
export function allSectionsRights(sections: readonly ShareSection[], role: 'meelezen' | 'meeschrijven'): Readonly<Record<string, 'lezen' | 'bewerken'>> {
  return Object.fromEntries(
    sections.filter((section) => section.verborgen !== true).map((section) => [section.id, role === 'meeschrijven' && section.magBewerken ? 'bewerken' : 'lezen']),
  )
}

/**
 * De zinnen onder "Alle secties": bij meeschrijven per reden de secties die alleen te lezen zijn, en
 * altijd welke verborgen secties niet meegaan. Leeg als er niets te melden is.
 */
export function allSectionsNotes(
  sections: readonly ShareSection[],
  role: 'meelezen' | 'meeschrijven',
  language: Language,
  /** Bij Rechten wijzigen: de rechten die er nu zijn. Een verborgen sectie daarin houdt haar recht (`allSectionsRightsKeeping`). */
  current?: Readonly<Record<string, 'lezen' | 'bewerken'>>,
): readonly string[] {
  const shown = sections.filter((section) => section.verborgen !== true)
  const notes: string[] = []
  if (role === 'meeschrijven') {
    const order: readonly WriteBlock[] = ['toetser', 'toetssectie', 'bronsectie', 'zelf-lezen']
    for (const reason of order) {
      const group = shown.filter((section) => !section.magBewerken && (section.schrijfReden ?? 'zelf-lezen') === reason).map(sectionLabel)
      if (group.length > 0) notes.push(READ_ONLY_GROUP[reason][language].replace('{s}', joinList(group, language)))
    }
  }
  const hiddenSections = sections.filter((section) => section.verborgen === true)
  const kept = hiddenSections.filter((section) => current?.[section.id] !== undefined).map(sectionLabel)
  const hidden = hiddenSections.filter((section) => current?.[section.id] === undefined).map(sectionLabel)
  if (kept.length > 0) notes.push(HIDDEN_KEPT[kept.length === 1 ? 'een' : 'meer'][language].replace('{s}', joinList(kept, language)))
  if (hidden.length > 0) notes.push(HIDDEN_NOT_SHARED[hidden.length === 1 ? 'een' : 'meer'][language].replace('{s}', joinList(hidden, language)))
  return notes
}

/**
 * "Alle secties" bij Rechten wijzigen van een bestaande deling (opdracht van 2 november 2026, ontwerp 6.4).
 *
 * Wat `allSectionsRights` geeft voor elke sectie die nu in het formulier staat, met de rol die de deling al
 * heeft. Een recht op een sectie die daar niet in telt, omdat een regel haar nu verbergt of omdat de deler
 * haar niet ziet, blijft staan zoals het was: wie Alle secties kiest, bedoelt meer te geven en niet stil iets
 * in te trekken wat bij het uitnodigen bewust is meegegeven. Intrekken op een sectie gaat met Alleen bepaalde
 * secties, en de zin onder de keuze zegt welke secties hun recht houden.
 */
export function allSectionsRightsKeeping(
  current: Readonly<Record<string, 'lezen' | 'bewerken'>>,
  sections: readonly ShareSection[],
  role: 'meelezen' | 'meeschrijven',
): Readonly<Record<string, 'lezen' | 'bewerken'>> {
  const shown = new Set(sections.filter((section) => section.verborgen !== true).map((section) => section.id))
  return { ...Object.fromEntries(Object.entries(current).filter(([id]) => !shown.has(id))), ...allSectionsRights(sections, role) }
}

/**
 * Of de rechten van een deling nu precies "Alle secties" zijn voor haar rol: dan opent Rechten wijzigen op die
 * keuze, en anders op Alleen bepaalde secties met de huidige rechten aangevinkt.
 */
export function isAllSections(
  current: Readonly<Record<string, 'lezen' | 'bewerken'>>,
  sections: readonly ShareSection[],
  role: 'meelezen' | 'meeschrijven',
): boolean {
  const all = allSectionsRights(sections, role)
  const ids = Object.keys(all)
  return ids.length > 0 && ids.every((id) => current[id] === all[id])
}

/** "A, B en C". */
export function joinList(items: readonly string[], language: Language): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} ${T.en[language]} ${items.at(-1) ?? ''}`
}

/** Hoe een sectie in een lijst of zin heet: met haar nummer als de pagina het meegaf. */
export function sectionLabel(section: Pick<ShareSection, 'titel' | 'nummer'>): string {
  return section.nummer === undefined ? section.titel : `${section.nummer} ${section.titel}`
}

/** De geldigheid van een uitnodiging in dagen, uit het model. */
export const INVITATION_VALID_DAYS = Math.round(INVITATION_VALID_MS / (24 * 60 * 60 * 1000))

/**
 * De samenvatting van één zin die onder het formulier meeloopt (besluit 1, A1).
 *
 * Wie: het adres zodra het is ingevuld, anders "deze persoon". Bij advies: welke secties, en dat de
 * adviseur leest en antwoordt en niets wijzigt.
 */
export function inviteSummary(
  input:
    | {
        readonly rol: 'meelezen' | 'meeschrijven'
        readonly email: string
        readonly rechten: Readonly<Record<string, 'lezen' | 'bewerken'>>
        /** Gekozen als "Alle secties": dan tellen in plaats van elke sectie bij naam. */
        readonly alle?: boolean
      }
    | { readonly rol: 'advies'; readonly email: string; readonly gedeeld: readonly string[] },
  sections: readonly ShareSection[],
  language: Language,
): string {
  const address = input.email.trim()
  const byId = new Map(sections.map((section) => [section.id, section]))
  if (input.rol === 'advies') {
    const shared = input.gedeeld.flatMap((id) => {
      const section = byId.get(id)
      return section === undefined ? [] : [sectionLabel(section)]
    })
    if (shared.length === 0) return T.geenAdviesSectie[language]
    const what = (shared.length === 1 ? T.sectie : T.secties)[language].replace('{s}', joinList(shared, language))
    return T.advies[language].replace('{secties}', what).replace('{wie}', address === '' ? T.deZePersoon[language] : address)
  }
  const who = address === '' ? T.deze[language] : address
  const chosen = sections.filter((section) => input.rechten[section.id] !== undefined)
  if (chosen.length === 0) return T.geenSectie[language].replace('{wie}', who)
  const write = chosen.filter((section) => input.rechten[section.id] === 'bewerken').map((section) => section.titel)
  const read = chosen.filter((section) => input.rechten[section.id] === 'lezen').map((section) => section.titel)
  // Bij "Alle secties" geen zin met dertien namen: het aantal, en bij naam alleen wat afwijkt.
  const counted = input.alle === true && chosen.length > 1
  const n = String(chosen.length)
  const first = counted
    ? write.length === 0
      ? T.alleLezen[language].replace('{n}', n)
      : read.length === 0
        ? T.alleSchrijven[language].replace('{n}', n)
        : T.deelsSchrijven[language].replace('{w}', String(write.length)).replace('{n}', n).replace('{a}', joinList(read, language))
    : write.length > 0
      ? `${T.schrijven[language].replace('{a}', joinList(write, language))}${read.length > 0 ? T.enLezen[language].replace('{a}', joinList(read, language)) : ''}`
      : T.lezen[language].replace('{a}', joinList(read, language))
  const rest = chosen.length < sections.length ? ` ${T.rest[language]}` : ''
  return `${who} ${first}.${rest} ${T.geldig[language].replace('{d}', String(INVITATION_VALID_DAYS))}`
}

/** Een datum als "6 november", in de tijd van NWO zodat het scherm en de test hetzelfde zeggen. */
export function formatDay(moment: string, language: Language, withYear = false): string {
  return formatDateTime(moment, language, {
    day: 'numeric',
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'Europe/Amsterdam',
  })
}

const ROLE_WORD: Readonly<Record<ShareRole, Bilingual>> = {
  meelezen: { nl: 'Meelezen', en: 'Read along' },
  meeschrijven: { nl: 'Meeschrijven', en: 'Co-write' },
  advies: { nl: 'Advies geven', en: 'Give advice' },
}

export function roleWord(role: ShareRole, language: Language): string {
  return ROLE_WORD[role][language]
}

/** De rol die bij rechten hoort: meeschrijven zodra er één sectie te schrijven is. */
export function roleOf(rights: Readonly<Record<string, 'lezen' | 'bewerken'>>): 'meelezen' | 'meeschrijven' {
  return Object.values(rights).includes('bewerken') ? 'meeschrijven' : 'meelezen'
}

/**
 * De rechten in woorden, met de secties bij naam: "Meeschrijven: Overzicht, Werkpakketten ·
 * meelezen: Probleemanalyse". Een sectie die de kijker niet heeft, staat er niet: geen id in beeld,
 * en geen titel van iets wat hij niet ziet (G-04).
 */
export function rightsInWords(
  rights: Readonly<Record<string, 'lezen' | 'bewerken'>>,
  sectionName: (sectionId: string) => string | undefined,
  language: Language,
  advice = false,
): string {
  const names = (permission: 'lezen' | 'bewerken') =>
    Object.entries(rights).flatMap(([id, given]) => {
      if (given !== permission) return []
      const name = sectionName(id)
      return name === undefined ? [] : [name]
    })
  if (advice) {
    const read = [...names('lezen'), ...names('bewerken')]
    return read.length === 0 ? ROLE_WORD.advies[language] : `${ROLE_WORD.advies[language]}: ${read.join(', ')}`
  }
  const write = names('bewerken')
  const read = names('lezen')
  const role = roleOf(rights)
  if (role === 'meelezen') return read.length === 0 ? ROLE_WORD.meelezen[language] : `${ROLE_WORD.meelezen[language]}: ${read.join(', ')}`
  const writePart = write.length === 0 ? ROLE_WORD.meeschrijven[language] : `${ROLE_WORD.meeschrijven[language]}: ${write.join(', ')}`
  return read.length === 0 ? writePart : `${writePart} · ${ROLE_WORD.meelezen[language].toLowerCase()}: ${read.join(', ')}`
}

const SHARER: Readonly<Record<Exclude<SharerRole, 'gemachtigde'>, Bilingual>> = {
  u: { nl: 'uitgenodigd door u', en: 'invited by you' },
  aanvrager: { nl: 'uitgenodigd door de aanvrager', en: 'invited by the applicant' },
  'medewerker-nwo': { nl: 'uitgenodigd door een medewerker van NWO', en: 'invited by an NWO employee' },
}

/** Wie het namens de kijker is, voor "uw gemachtigde": alleen de aanvrager heeft een gemachtigde. */
export function viewerIsApplicant(grants: readonly OwnGrant[]): boolean {
  return grants.some((grant) => grant.isU && grant.rol === 'aanvrager')
}

/** De rol van wie uitnodigde, als de pod hem noemt; bij een pod zonder `delerRol` alleen "door u". */
function sharerOf(line: ShareLine): SharerRole | undefined {
  return line.delerRol ?? (line.eigen ? 'u' : undefined)
}

/** "uitgenodigd door uw gemachtigde" (besluit 5), of niets als de pod de rol niet noemt. */
export function invitedBy(line: ShareLine, applicant: boolean, language: Language): string | undefined {
  const role = sharerOf(line)
  if (role === undefined) return undefined
  if (role === 'gemachtigde') {
    return applicant
      ? { nl: 'uitgenodigd door uw gemachtigde', en: 'invited by your authorised representative' }[language]
      : { nl: 'uitgenodigd door de gemachtigde', en: 'invited by the authorised representative' }[language]
  }
  return SHARER[role][language]
}

/** Wiens toegang eindigde bij een vervallen regel (R-65e). */
function lapsedWhose(role: SharerRole | undefined, applicant: boolean, language: Language): string {
  switch (role) {
    case 'gemachtigde':
      return applicant
        ? { nl: 'uw gemachtigde', en: 'your authorised representative' }[language]
        : { nl: 'de gemachtigde', en: 'the authorised representative' }[language]
    case 'aanvrager':
      return { nl: 'de aanvrager', en: 'the applicant' }[language]
    case 'medewerker-nwo':
      return { nl: 'de medewerker van NWO die uitnodigde', en: 'the NWO employee who invited' }[language]
    default:
      return { nl: 'wie uitnodigde', en: 'whoever invited' }[language]
  }
}

/** De stand van een regel: een label (bij wat niet gewoon loopt) en een regel met datum en reden. */
export interface StateText {
  readonly label?: string
  readonly tekst: string
}

export function stateText(line: ShareLine, applicant: boolean, language: Language): StateText {
  const since = line.sinds ?? line.aangemaaktOp
  switch (line.stand) {
    case 'uitgenodigd':
      return {
        label: { nl: 'Nog niet aangenomen', en: 'Not yet accepted' }[language],
        tekst: { nl: 'Geldig tot {d}', en: 'Valid until {d}' }[language].replace('{d}', formatDay(line.geldigTot, language)),
      }
    case 'binnengekomen':
      return {
        tekst:
          line.sinds === null
            ? { nl: 'Heeft toegang', en: 'Has access' }[language]
            : { nl: 'Toegang sinds {d}', en: 'Access since {d}' }[language].replace('{d}', formatDay(line.sinds, language)),
      }
    case 'geweigerd':
      return {
        label: { nl: 'Geweigerd', en: 'Declined' }[language],
        tekst: { nl: 'Geweigerd op {d}', en: 'Declined on {d}' }[language].replace('{d}', formatDay(since, language)),
      }
    case 'verlopen':
      return {
        label: { nl: 'Verlopen', en: 'Expired' }[language],
        tekst: { nl: 'Niet aangenomen binnen {n} dagen, verlopen op {d}', en: 'Not accepted within {n} days, expired on {d}' }[language]
          .replace('{n}', String(INVITATION_VALID_DAYS))
          .replace('{d}', formatDay(line.geldigTot, language)),
      }
    case 'ingetrokken':
      return {
        label: { nl: 'Ingetrokken', en: 'Withdrawn' }[language],
        tekst: { nl: 'Ingetrokken op {d}', en: 'Withdrawn on {d}' }[language].replace('{d}', formatDay(since, language)),
      }
    case 'vervallen':
      return {
        label: { nl: 'Vervallen', en: 'Lapsed' }[language],
        tekst: { nl: 'Vervallen: de toegang van {wie} is beëindigd', en: 'Lapsed: the access of {wie} has ended' }[language].replace(
          '{wie}',
          lapsedWhose(sharerOf(line), applicant, language),
        ),
      }
  }
}

const ADVICE_STATE: Readonly<Record<AdviesToestand, Bilingual>> = {
  gevraagd: { nl: 'Gevraagd', en: 'Requested' },
  beantwoord: { nl: 'Beantwoord', en: 'Answered' },
  afgerond: { nl: 'Afgerond', en: 'Completed' },
  ingetrokken: { nl: 'Ingetrokken', en: 'Withdrawn' },
  vervallen: { nl: 'Vervallen', en: 'Lapsed' },
  'belang-gemeld': { nl: 'Belang gemeld', en: 'Interest declared' },
}

export function adviceStateWord(state: AdviesToestand, language: Language): string {
  return ADVICE_STATE[state][language]
}

/** Het onderwerp van een adviesverzoek in een regel: "over het formulier", "over Begroting". */
export function adviceAbout(anchor: AdviceLine['anker'], language: Language): string {
  if (anchor.soort === 'formulier') return { nl: 'over het formulier', en: 'about the form' }[language]
  return { nl: 'over {a}', en: 'about {a}' }[language].replace('{a}', anchor.label)
}

/** Wat de kijker met een regel mag; de pod zegt het, en een oudere pod niet (zie `ShareLine`). */
export function running(line: ShareLine): boolean {
  return line.stand === 'uitgenodigd' || line.stand === 'binnengekomen'
}

export function mayWithdraw(line: ShareLine): boolean {
  return line.intrekbaar ?? (line.eigen && running(line))
}

export function mayChange(line: ShareLine): boolean {
  return (line.wijzigbaar ?? false) && running(line)
}

/** Een rij in de lijst: een toekenning van de eigen kant, of een regel met misschien een adviesverzoek erbij. */
export type AccessRow =
  | { readonly soort: 'toekenning'; readonly toekenning: OwnGrant }
  | { readonly soort: 'regel'; readonly regel: ShareLine; readonly advies?: AdviceLine }
  | { readonly soort: 'advies'; readonly advies: AdviceLine }

export interface AccessGroups {
  /** Heeft toegang: de eigen kant uit het processysteem, en wie aannam. */
  readonly toegang: readonly AccessRow[]
  /** Uitnodigingen: nog niet aangenomen, en verlopen (dan met Opnieuw uitnodigen). */
  readonly uitnodigingen: readonly AccessRow[]
  /** Eerder: geweigerd, ingetrokken, vervallen, en een geëindigd adviesverzoek. */
  readonly eerder: readonly AccessRow[]
}

function groupOf(row: AccessRow): keyof AccessGroups {
  if (row.soort === 'toekenning') return 'toegang'
  if (row.advies !== undefined && !isOpenAdvies(row.advies.toestand)) return 'eerder'
  if (row.soort === 'advies') return 'toegang'
  switch (row.regel.stand) {
    case 'binnengekomen':
      return 'toegang'
    case 'uitgenodigd':
    case 'verlopen':
      return 'uitnodigingen'
    default:
      return 'eerder'
  }
}

/**
 * De lijst in drie groepen (mockup 03 van delen, 04 van advies). Toekenningen bovenaan, met de kijker
 * zelf eerst; een adviesverzoek bij zijn regel; `nieuw` (net verstuurd) bovenaan zijn groep.
 */
export function groupAccess(
  input: { readonly delingen: readonly ShareLine[]; readonly toekenningen: readonly OwnGrant[]; readonly advies: readonly AdviceLine[] },
  newId?: string,
): AccessGroups {
  const byInvitation = new Map(input.advies.map((advice) => [advice.uitnodigingId, advice]))
  const rows: AccessRow[] = [
    ...[...input.toekenningen].sort((a, b) => Number(b.isU) - Number(a.isU)).map((toekenning) => ({ soort: 'toekenning' as const, toekenning })),
    ...input.delingen.map((regel) => {
      const advies = byInvitation.get(regel.id)
      return advies === undefined ? { soort: 'regel' as const, regel } : { soort: 'regel' as const, regel, advies }
    }),
  ]
  const linked = new Set(input.delingen.map((line) => line.id))
  for (const advice of input.advies) if (!linked.has(advice.uitnodigingId)) rows.push({ soort: 'advies', advies: advice })
  const isNew = (row: AccessRow) =>
    newId !== undefined && ((row.soort === 'regel' && row.regel.id === newId) || (row.soort !== 'toekenning' && row.advies?.id === newId))
  const groups: Record<keyof AccessGroups, AccessRow[]> = { toegang: [], uitnodigingen: [], eerder: [] }
  for (const row of rows) groups[groupOf(row)].push(row)
  for (const group of Object.values(groups)) group.sort((a, b) => Number(isNew(b)) - Number(isNew(a)))
  return groups
}

const GRANT_ROLE: Readonly<Record<string, Bilingual>> = {
  aanvrager: { nl: 'Aanvrager', en: 'Applicant' },
  gemachtigde: { nl: 'Gemachtigde', en: 'Authorised representative' },
  invuller: { nl: 'Invuller', en: 'Filler' },
  toetser: { nl: 'Toetser', en: 'Reviewer' },
}

/** De rol van een toekenning in woorden; een rol die het scherm niet kent, als "Toegang via NWO". */
export function grantRoleWord(role: string, language: Language): string {
  return GRANT_ROLE[role]?.[language] ?? { nl: 'Toegang via NWO', en: 'Access through NWO' }[language]
}
